import JSZip from '@turbowarp/jszip';

const BASE_URL = process.env.MAGNUM_API_URL;
const APP_URL = process.env.MAGNUM_APP_URL;

const jsonHeaders = {'Content-Type': 'application/json'};

const safeFetch = async (url, options) => {
    try {
        return await fetch(url, options);
    } catch (_) {
        throw new Error('Something went wrong. Please try again later.');
    }
};

export class UnauthorizedError extends Error {
    constructor () {
        super('Your session has expired. Please sign in again to continue.');
        this.name = 'UnauthorizedError';
    }
}

const handleResponse = async resp => {
    if (resp.status === 401) {
        throw new UnauthorizedError();
    }
    if (!resp.ok) {
        if (resp.status >= 500) {
            throw new Error('Something went wrong. Please try again later.');
        }
        let body;
        try {
            body = await resp.json();
        } catch (_) {
            body = {error: resp.statusText};
        }
        throw new Error(body.error || `HTTP ${resp.status}`);
    }
    return resp.json();
};

/**
 * Parse an .sb3 Blob into { projectJson, assets[] }.
 * assets: [{ md5, ext, data: base64 }]
 */
export const parseSb3 = async blob => {
    const zip = await JSZip.loadAsync(blob);
    const projectJsonEntry = zip.file('project.json');
    if (!projectJsonEntry) throw new Error('Invalid .sb3: missing project.json');
    const projectJsonStr = await projectJsonEntry.async('string');
    const projectJson = JSON.parse(projectJsonStr);

    const assets = [];
    const assetFiles = Object.keys(zip.files).filter(name => name !== 'project.json');
    await Promise.all(assetFiles.map(async name => {
        // asset filenames are "<md5>.<ext>"
        const dotIdx = name.lastIndexOf('.');
        if (dotIdx === -1) return;
        const md5 = name.slice(0, dotIdx);
        const ext = name.slice(dotIdx + 1);
        const entry = zip.file(name);
        if (!entry) return;
        const data = await entry.async('base64');
        assets.push({md5, ext, data});
    }));

    return {projectJson, assets};
};

/**
 * Reconstruct an .sb3 Blob from { projectJson, assets[] }.
 * assets: [{ md5, ext, data: base64 }]
 */
export const buildSb3 = async ({projectJson, assets}) => {
    const zip = new JSZip();
    zip.file('project.json', JSON.stringify(projectJson));
    for (const {md5, ext, data} of assets) {
        zip.file(`${md5}.${ext}`, data, {base64: true});
    }
    return zip.generateAsync({type: 'arraybuffer'});
};

// ─── Cloud API ────────────────────────────────────────────────────────────────

export const silentAuth = async () => {
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/silent-auth`, {
        credentials: 'include'
    });
    const {user} = await resp.json();
    return user;
};

export const listProjects = async () => {
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/projects`, {
        credentials: 'include'
    });
    const body = await handleResponse(resp);
    return body.projects;
};

export const createProject = async (name, sb3Blob) => {
    const {projectJson, assets} = await parseSb3(sb3Blob);
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/projects`, {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({name, projectJson, assets})
    });
    return handleResponse(resp);
};

export const updateProject = async (id, name, sb3Blob) => {
    const {projectJson, assets} = await parseSb3(sb3Blob);
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/projects/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({name, projectJson, assets})
    });
    return handleResponse(resp);
};

export const fetchProject = async id => {
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/projects/${id}`, {
        credentials: 'include'
    });
    return handleResponse(resp);
};

export const deleteProject = async id => {
    const resp = await safeFetch(`${BASE_URL}/api/magblocks/projects/${id}`, {
        method: 'DELETE',
        credentials: 'include'
    });
    return handleResponse(resp);
};

// ─── Local (.mgnm) ────────────────────────────────────────────────────────────

const blobToBase64 = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(/** @type {string} */ (reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const base64ToArrayBuffer = b64 => {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
};

export const exportMgnm = async (sb3Blob, projectName) => {
    const sb3Base64 = await blobToBase64(sb3Blob);
    const envelope = JSON.stringify({
        version: '1.0',
        editorType: 'magblocks',
        createdAt: new Date().toISOString(),
        sb3: sb3Base64
    });
    const outBlob = new Blob([envelope], {type: 'application/json'});
    const url = URL.createObjectURL(outBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'project'}.mgnm`;
    a.click();
    URL.revokeObjectURL(url);
};

export const importMgnm = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        let parsed;
        try {
            parsed = JSON.parse(/** @type {string} */ (reader.result));
        } catch (_) {
            return reject(new Error('Invalid project file'));
        }
        if (parsed.editorType !== 'magblocks') {
            return reject(new Error('This project is for a different editor'));
        }
        if (!parsed.sb3) {
            return reject(new Error('Invalid project file'));
        }
        resolve(base64ToArrayBuffer(parsed.sb3));
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file);
});
